import React, {
  Fragment,
  useState,
  cloneElement,
  FC,
  ReactElement,
  useEffect,
} from 'react';
import {
  List,
  TextField,
  FunctionField,
  sanitizeListRestProps,
  TopToolbar,
  useListContext,
  ListProps,
  PublicFieldProps,
  InjectedFieldProps,
  ToolbarProps,
  ExportButtonProps,
  useUnselectAll,
} from 'react-admin';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import InfoDialog from 'components/InfoDialog';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Show } from 'components/Show';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import Datagrid from 'components/Datagrid';
import { ProdPayMasterSheetFilter } from '../filters/prod-pay-master-sheet-filters';
import { makeStyles } from '@material-ui/core';
import { exporter } from 'providers/export';
import {
  JobPhaseInterface,
  ProdPayMasterSheetInterface,
} from '@vatos-pas/common';
import ContractorHistoryDialog from '../../master-sheet-v2/components/Timeline/ContractorHistoryDialog';
import {
  CAN_EXPORT_ACCOUNTING_REPORT,
  CAN_PAID_PHASE,
  CAN_SEE_LOT_REPORT,
} from 'providers/permissions';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { UIProvider } from 'providers/uiProvider';
import { WithoutTimezoneDateField } from 'modules/master-sheet-v2/views';
import AccountingExportButton from '../components/AccountingExport';
import { API_MAX_ROWS } from 'providers/apiLimits';
import PaidStatusField from '../components/PaidStatusField';
import TotalCostField from '../components/TotalCostField';
import BulkActionButtons from '../components/BulkActionButtons';
import CustomExportButton from 'components/CustomExportButton';
import { SupervisorField } from 'components/SupervisorListItems';
import { trackEvent } from 'utils/mixpanel';

const useStyles = makeStyles({
  content: {
    '@media(max-width: 1300px)': {
      overflow: 'auto',
    },
  },
  contentClosed: {
    '@media(max-width: 1300px)': {
      overflow: 'auto',
    },
  },
  root: {
    '@media(max-width: 1550px)': {
      width: 'calc(100vw - 260px)',
    },
    '@media(max-width: 850px)': {
      width: 'calc(100vw - 45px)',
      marginLeft: '15px',
    },
  },
  rootClosed: {
    '@media(max-width: 1550px)': {
      width: 'calc(100vw - 125px)',
    },
    '@media(max-width: 850px)': {
      width: 'calc(100vw - 45px)',
      marginLeft: '15px',
    },
  },
  // RA's list toolbar never wraps, so on phones the filters plus the export
  // buttons overflow the viewport; let the actions drop below the filters.
  wrapToolbar: {
    '@media(max-width: 599.95px)': {
      '& > .MuiToolbar-root': {
        flexWrap: 'wrap',
      },
    },
  },
  link: {
    backgroundColor: '#e6005a',
    height: '50px',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  },
  linkText: {
    color: 'white',
  },
  pink: {
    backgroundColor: 'lightpink',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  normal: {
    cursor: 'pointer',
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
  infoWarning: {
    color: '#ff9800',
  },
});

type ListActionProps = ToolbarProps &
  Pick<ExportButtonProps, 'maxResults'> & {
    filters?: ReactElement;
  };

const ListActions: FC<ListActionProps> = ({
  className,
  filters,
  maxResults,
  ...rest
}) => {
  const {
    currentSort,
    resource,
    displayedFilters,
    filterValues,
    showFilter,
    total,
  } = useListContext();
  const { hasPermission } = PermissionsProvider.useContainer();

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      {hasPermission(CAN_EXPORT_ACCOUNTING_REPORT) ? (
        <AccountingExportButton total={total} />
      ) : null}
      <CustomExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filterValues={filterValues}
        maxResults={maxResults}
      />
    </TopToolbar>
  );
};

type LotReportProps = {
  classes: { [key: string]: string };
} & PublicFieldProps &
  InjectedFieldProps<ProdPayMasterSheetInterface>;

const LotReport: FC<LotReportProps> = ({ record, classes }) => {
  if (!record) return null;

  // Route to V1 for Drywall, V2 for DrywallAndPaint
  const route =
    record.phaseStructureType === 'DrywallAndPaint'
      ? 'job-master-sheet-v2'
      : 'job-master-sheet';

  return (
    <Box className={!record.jobHasSpecialPayment ? '' : classes.link}>
      <Typography>
        <Link
          className={!record.jobHasSpecialPayment ? '' : classes.linkText}
          href={`${window.location.origin}/#/${route}/${record.jobId}`}
        >
          View
        </Link>
      </Typography>
    </Box>
  );
};

type ContractorFieldProps = {
  setOpenHistoryModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedJobPhasel: React.Dispatch<
    React.SetStateAction<
      | {
          jobPhaseContractorHistories: JobPhaseInterface['jobPhaseContractorHistories'];
        }
      | undefined
    >
  >;
  classes: { [key: string]: string };
} & PublicFieldProps &
  InjectedFieldProps<ProdPayMasterSheetInterface>;

const ContractorField: FC<ContractorFieldProps> = ({
  record,
  setOpenHistoryModal,
  setSelectedJobPhasel,
  classes,
}) => {
  if (!record) return null;

  const { jobPhaseContractorHistories, contractorName } = record;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openHistory = () => {
    setOpenHistoryModal(true);
    setSelectedJobPhasel({ jobPhaseContractorHistories });
  };

  const jobPhaseContractorHistoriesLength = jobPhaseContractorHistories?.length;

  return (
    <Box display="flex" alignItems="center" gridColumnGap={4}>
      <Typography
        className={
          jobPhaseContractorHistoriesLength &&
          jobPhaseContractorHistoriesLength > 1
            ? classes.pink
            : classes.normal
        }
        onClick={openHistory}
      >
        {contractorName}
      </Typography>
      <Show condition={record.contractorHasNotes}>
        <IconButton onClick={() => setIsModalOpen(true)}>
          <InfoOutlinedIcon className={classes.infoWarning} />
        </IconButton>
      </Show>
      <InfoDialog
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        title="Contractor Notes"
        content={
          <>
            {record.contractorHasNotes && (
              <Typography>{record.contractorNotes}</Typography>
            )}
          </>
        }
      />
    </Box>
  );
};

export const ProdPayMasterSheetList: FC<ListProps> = props => {
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedJobPhase, setSelectedJobPhasel] = useState<{
    jobPhaseContractorHistories: JobPhaseInterface['jobPhaseContractorHistories'];
  }>();
  const { opened } = UIProvider.useContainer();
  const classes = useStyles(props);

  const { hasPermission } = PermissionsProvider.useContainer();
  // One flag gates the row checkboxes, Select all and the Mark as Paid bar.
  const canMarkPaid = hasPermission(CAN_PAID_PHASE);

  // The store keeps a selection after the list unmounts; rows chosen on an
  // earlier visit must not be paid from this one.
  const unselectAll = useUnselectAll(props.resource);
  useEffect(() => {
    unselectAll();
  }, [unselectAll]);

  useEffect(() => {
    trackEvent('PageView', {
      resource: 'prod-pay',
      page: window.location.pathname,
      role: localStorage.getItem('userRoles'),
      env: process.env.REACT_APP_MIXPANEL_ENV,
    });
  }, []);

  return (
    <Fragment>
      <List
        classes={{
          content: opened ? classes.content : classes.contentClosed,
          root: `${opened ? classes.root : classes.rootClosed} ${
            classes.wrapToolbar
          }`,
        }}
        {...props}
        exporter={exporter('prod-pay-master-sheet')}
        filters={
          <ProdPayMasterSheetFilter>{props.filters}</ProdPayMasterSheetFilter>
        }
        filterDefaultValues={{ isPaid: false }}
        sort={{ field: 'dateApproved', order: 'ASC' }}
        bulkActionButtons={canMarkPaid && <BulkActionButtons />}
        actions={<ListActions maxResults={API_MAX_ROWS} />}
      >
        <Datagrid selectAllRows={canMarkPaid}>
          <SupervisorField label="Supervisors" />
          <TextField source="regionName" label="Region" />
          <TextField source="builderName" label="Builder" />
          <FunctionField
            source="phaseStructureType"
            label="Workflow"
            render={record =>
              record?.phaseStructureType === 'DrywallAndPaint'
                ? 'Drywall and Paint'
                : 'Drywall'
            }
          />
          <TextField source="subdivisionName" label="Subdivision" />
          <TextField source="jobLot" label="Lot" />
          <TextField source="jobBlock" label="Block" />
          <TextField source="modelName" label="Model" />
          <TextField source="phaseName" label="Phase" />
          <FunctionField
            source="houseMarked"
            label="Marked House"
            render={record => (
              <Checkbox
                disabled
                checked={!!record?.houseMarked}
                inputProps={{ 'aria-label': 'Marked House' }}
              />
            )}
          />
          <ContractorField
            setSelectedJobPhasel={setSelectedJobPhasel}
            label="Contractor"
            setOpenHistoryModal={setOpenHistoryModal}
            classes={classes}
          />
          <FunctionField
            label="Payable Boards"
            render={record => {
              const value = record?.payableBoardsTotal;
              return value == null ||
                value === '' ||
                value === 0 ||
                value === '0'
                ? '–'
                : value;
            }}
          />
          <FunctionField
            label="Supervisor Count"
            render={record => {
              const value = record?.payableBoardsReceiptTotal;
              return value == null ||
                value === '' ||
                value === 0 ||
                value === '0'
                ? '–'
                : value;
            }}
          />
          <FunctionField
            label={'Payable Sq\nFt'}
            render={record => {
              const value = record?.totalSquareFootage;
              return value == null ||
                value === '' ||
                value === 0 ||
                value === '0'
                ? '–'
                : value;
            }}
          />
          {hasPermission(CAN_SEE_LOT_REPORT) && (
            <LotReport classes={classes} label="Lot Report" />
          )}
          <WithoutTimezoneDateField
            source="dateApproved"
            label="Completion Date"
          />
          <PaidStatusField label="Paid?" />
          <TotalCostField label="Cost Override" />
        </Datagrid>
      </List>
      <ContractorHistoryDialog
        handleClose={() => setOpenHistoryModal(false)}
        open={openHistoryModal}
        selectedJobPhase={selectedJobPhase}
      />
    </Fragment>
  );
};
